import { Box } from "../box/Box";
import { Typo } from "../text/Typo";
import { InputBoxStyle, InputEl } from "../input/Input";
import { ChangeEventHandler, FC, useCallback, useEffect, useRef, useState } from "react";
import { CSSProperties } from "styled-components";

type Props = {
	label?: string;
	ariaLabel?: string;
	disabled?: boolean;
	readOnly?: boolean;
	focused?: boolean;
	sx?: CSSProperties;
	sxInput?: CSSProperties;
	inputColor?: string;
	size?: string;
	value?: Date;
	name?: string;
	min?: Date | null;
	returnType?: "Date" | "ISOLocalString";
	onChange?: (value: Date | string) => void;
};

export const DatePicker: FC<Props> = ({ label, ariaLabel, disabled, readOnly, sx, sxInput, inputColor, size, value, name, min, returnType = "Date", onChange }) => {
	const [today] = useState(() => new Date());
	const [date, setDate] = useState<Date | undefined>(() => normalizeDate(value));
	const [focused, setFocused] = useState<boolean>(false);
	const [id] = useState(() => window.crypto.randomUUID().replaceAll("-", ""));
	const inputRef = useRef<HTMLInputElement>(null);
	const [isTouchDevice] = useState(() => window.matchMedia("(pointer: coarse)").matches);

	useEffect(() => {
		setDate(normalizeDate(value));
	}, [value]);

	const onFocus = () => {
		setFocused(true);
	};

	const onBlur = () => {
		setFocused(false);
	};

	const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
		event => {
			const nextDate = convertToDate(event.target.value);
			setDate(nextDate);

			if (returnType === "Date") {
				onChange?.(nextDate ?? new Date(""));
			} else if (returnType === "ISOLocalString") {
				onChange?.(nextDate?.toISOString() ?? "");
			}
		},
		[onChange, returnType],
	);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: "3px",
			}}>
			{label && (
				<Typo variant="caption">
					<label htmlFor={id}>{label}:</label>
				</Typo>
			)}
			<Box
				sx={{
					flexGrow: 1,
					boxSizing: "border-box",
					display: "flex",

					...InputBoxStyle(disabled ?? false, readOnly ?? false),

					"&:hover": {
						//backgroundColor:"#e6e3dd", //F0EEEA
						border: disabled ? undefined : "1px solid #1a5bff",
						transition: "border .2s",
					},

					"@media (prefers-color-scheme: dark)": {
						//backgroundColor:"#29272f",
					},

					...sx,

					...(focused ? { border: "1px solid #1a5bff" } : {}),
				}}>
				<InputEl
					aria-label={ariaLabel || label || name}
					ref={inputRef}
					onFocus={onFocus}
					onBlur={onBlur}
					disabled={disabled}
					$readOnly={readOnly}
					$sx={sxInput}
					$color={inputColor}
					$size={size === "small" ? "small" : "medium"}
					onChange={handleChange}
					onClick={
						isTouchDevice
							? () => {
									try {
										inputRef.current?.showPicker();
									} catch {}
								}
							: undefined
					}
					value={convetToInputFormat(date)}
					min={min === null ? undefined : convetToInputFormat(min ?? today)}
					name={name}
					type="date"
					id={id}
				/>
			</Box>
		</Box>
	);
};

const DATE_PART_LENGTH = 2;

function convetToInputFormat(date?: Date): string {
	if (!isValidDate(date)) {
		return "";
	}

	// Local calendar day: toISOString() would shift the day for non-UTC time zones.
	const pad = (part: number) => String(part).padStart(DATE_PART_LENGTH, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function convertToDate(date: string): Date | undefined {
	if (!date) {
		return undefined;
	}

	// "YYYY-MM-DD" alone parses as UTC midnight; append a time so it parses as local midnight.
	const parsedDate = new Date(`${date}T00:00:00`);
	return isValidDate(parsedDate) ? parsedDate : undefined;
}

function normalizeDate(date?: Date): Date | undefined {
	return isValidDate(date) ? date : undefined;
}

function isValidDate(date?: Date): date is Date {
	return date instanceof Date && !Number.isNaN(date.getTime());
}
